import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import GlobalSpinner from '../shared/GlobalSpinner';
import AiCopilotFab from '../shared/AiCopilotFab';
import { useLang } from '../../context/LangContext';
import { t as dictT } from '../../i18n/dictionary';
import { useRoleContext } from '../../context/RoleContext';
import { ToastContainer, showToast } from '../ui/Toast';
import { ConfirmDialogContainer } from '../ui/ConfirmDialog';
import AlertsPanel from '../AlertsPanel';
import TrialBanner from '../TrialBanner';
import CompanyLimitBanner from '../CompanyLimitBanner';
import NoCompanySelectedBanner from '../NoCompanySelectedBanner';
// SEC-5: 60 min inactivity timeout. Warning 1 min before logout.
const INACTIVITY_TIMEOUT_MS = 60 * 60 * 1000;
const WARNING_BEFORE_MS     =  1 * 60 * 1000;
import { signOutWithAudit } from '../../utils/signOutWithAudit';
import { startInactivityMonitor } from '../../utils/inactivityMonitor';
import { useCompanyContext } from '../../context/CompanyContext';
import { useDailyTcSync } from '../../hooks/useDailyTcSync';

// P-3 (audit 2026-07-06): QuickGuide es el único consumidor de framer-motion
// en el árbol eager — lazy lo saca del main chunk. El FAB aparece unos ms
// después del primer paint, imperceptible.
const QuickGuide = lazy(() => import('../shared/QuickGuide'));

// Exportado (además de su tipo `Labels`, ya exportado) únicamente para que
// financialStatementNames.test.ts pueda comparar estos valores contra las
// otras fuentes de i18n. Sin cambio de comportamiento.
export const labels = {
  es: {
    config: 'Perfil Empresa', cargar: 'Cargar Archivos', xmlParser: 'Parser XML',
    procesar: 'Procesar Asientos', resultados: 'Panel', balance: 'Estado de Situación Financiera',
    pyg: 'Estado de Resultados', flujo: 'Estado de Flujos de Efectivo', cierre: 'Cierre Mensual',
    panel: 'Centro Tributario', libros: 'Libros Electrónicos', exportar: 'Exportar',
    journals: 'Asientos', libroDiario: 'Diario Contable', libroMayor: 'Libro Mayor',
    sumasSaldos: 'Sumas y Saldos', kardex: 'Kardex',
    dashboardEmpresarial: 'INICIO', motorProcesamiento: 'OPERACIÓN DIARIA',
    construccion: 'LIBROS CONTABLES', inteligenciaFinanciera: 'REPORTES FINANCIEROS',
    cumplimientoTributario: 'TRIBUTARIO', guiaBiblioteca: 'BIBLIOTECA',
    ayuda: 'Biblioteca', baseLegal: 'Base Legal', normasLegales: 'NIC y Normas',
    despacho: 'DESPACHO CONTABLE', consolaContador: 'Mi Despacho',
    ingresos: 'Ingresos', gastos: 'Gastos', flujoCajaEmp: 'Flujo de Caja',
    reportesPyg: 'Reportes P&G', impuestosEmp: 'Impuestos', ayudaEmp: 'Ayuda',
    salud: 'Salud del Negocio', ventas: 'Registro de Ventas',
    compras: 'Registro de Compras', impuestosIgv: 'Panel de impuestos', pdt621: 'Declaraciones',
    detracciones: 'Detracciones SPOT',
    categorizador: 'Maestro Contribuyentes', aiCategorizar: 'Categorizar con IA', auditoria: 'Auditoría',
    finanzas: 'FINANZAS', reportes: 'REPORTES EEFF',
    tributacion: 'TRIBUTACIÓN', biblioteca: 'GUÍA & BIBLIOTECA', periodo: 'Período',
    clientes: 'Clientes', operaciones: 'OPERACIONES',
    inteligencia: 'INTELIGENCIA', cumplimiento: 'CUMPLIMIENTO',
    bandejaBancaria: 'Movimientos Bancarios',
    bandejaClasificacion: 'Por Clasificar',
    inboxFallidos: 'Archivos Rechazados',
    prestamos: 'Préstamos Bancarios',
    asientosAutomaticos: 'Asientos Automáticos',
    agentes: 'Agentes',
    cuentasBancarias: 'Cuentas Bancarias',
    pleDiario51: 'Libro Diario', pleMayor61: 'Libro Mayor',
    pleCajaBancos: 'Caja y Bancos', pleBalance317: 'Balance Comprobación',
    settings: 'Configuración',
    aiAsistente: 'Asistente IA',
    cxp: 'Cuentas por Pagar',
    cxc: 'Cuentas por Cobrar',
    emailPreview: 'Email Preview',
    // Emprendedor nav
    miNegocio: 'MI NEGOCIO',
    resumen: 'Resumen',
    misFacturas: 'Mis Facturas',
    misPagos: 'Mis Pagos',
    misReportes: 'Mis Reportes',
    miSunat: 'SUNAT',
    soporte: 'SOPORTE',
    hablarContador: 'Hablar con mi contador',
    ayudaEmprendedor: 'Ayuda',
    // §18 — nuevos
    configuracionSection: 'CONFIGURACIÓN',
    miNegocioItem: 'Mi Negocio',
    perfilEmpresa: 'Perfil de Empresa',
    librosElectronicos: 'Libros Electrónicos',
    teamSettings: 'Asistentes',
  },
  en: {
    config: 'Company Profile', cargar: 'Upload Files', xmlParser: 'XML Parser',
    procesar: 'Process Entries', resultados: 'Dashboard', balance: 'Statement of Financial Position',
    pyg: 'Income Statement', flujo: 'Cash Flow Statement', cierre: 'Monthly Close',
    panel: 'Tax Center', libros: 'Electronic Books', exportar: 'Export',
    journals: 'Journal Entries', libroDiario: 'Accounting Journal', libroMayor: 'General Ledger',
    sumasSaldos: 'Trial Balance', kardex: 'Kardex',
    dashboardEmpresarial: 'HOME', motorProcesamiento: 'DAILY OPERATIONS',
    construccion: 'ACCOUNTING BOOKS', inteligenciaFinanciera: 'FINANCIAL REPORTS',
    cumplimientoTributario: 'TAX COMPLIANCE', guiaBiblioteca: 'LIBRARY',
    ayuda: 'Library', baseLegal: 'Legal Base', normasLegales: 'IFRS & Standards',
    despacho: 'ACCOUNTING OFFICE', consolaContador: 'My Office',
    ingresos: 'Revenue', gastos: 'Expenses', flujoCajaEmp: 'Cash Flow',
    reportesPyg: 'P&L Reports', impuestosEmp: 'Taxes', ayudaEmp: 'Help',
    salud: 'Business Health', ventas: 'Sales Register',
    compras: 'Purchase Register', impuestosIgv: 'Tax Dashboard', pdt621: 'Tax Returns',
    detracciones: 'SPOT Withholdings',
    categorizador: 'Taxpayer Master', aiCategorizar: 'AI Categorize', auditoria: 'Audit',
    finanzas: 'FINANCE', reportes: 'FINANCIAL REPORTS',
    tributacion: 'TAXATION', biblioteca: 'GUIDE & LIBRARY', periodo: 'Period',
    clientes: 'Clients', operaciones: 'OPERATIONS',
    inteligencia: 'INTELLIGENCE', cumplimiento: 'COMPLIANCE',
    bandejaBancaria: 'Bank Transactions',
    bandejaClasificacion: 'To Classify',
    inboxFallidos: 'Failed Uploads',
    prestamos: 'Bank Loans',
    asientosAutomaticos: 'Automatic Entries',
    agentes: 'Agents',
    cuentasBancarias: 'Bank Accounts',
    pleDiario51: 'Journal Book', pleMayor61: 'General Ledger',
    pleCajaBancos: 'Cash & Banks', pleBalance317: 'Trial Balance',
    settings: 'Settings',
    aiAsistente: 'AI Assistant',
    cxp: 'Accounts Payable',
    cxc: 'Accounts Receivable',
    emailPreview: 'Email Preview',
    // Emprendedor nav
    miNegocio: 'MY BUSINESS',
    resumen: 'Summary',
    misFacturas: 'My Invoices',
    misPagos: 'My Payments',
    misReportes: 'My Reports',
    miSunat: 'SUNAT',
    soporte: 'SUPPORT',
    hablarContador: 'Talk to my accountant',
    ayudaEmprendedor: 'Help',
    // §18 — nuevos
    configuracionSection: 'SETTINGS',
    miNegocioItem: 'My Business',
    perfilEmpresa: 'Company Profile',
    librosElectronicos: 'Electronic Books',
    teamSettings: 'Team',
  },
};

export type Lang = 'es' | 'en';
export type Mode = 'emprendedor' | 'contador';
export type Labels = (typeof labels)['es'];

export default function Layout() {
  const { lang } = useLang();
  const { isCollapsed } = useRoleContext();
  const { pathname } = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const { activeFirmId } = useCompanyContext();
  useDailyTcSync(activeFirmId); // auto-fetch today's SBS TC once per authenticated session

  const t = labels[lang as keyof typeof labels] ?? labels.es;

  // ── SEC-5: Inactivity auto-logout ────────────────────────────────
  // langRef lets the timer callback always read the current language
  // without adding lang to the effect dependency array (would reset timers on toggle).
  const langRef = useRef(lang);
  langRef.current = lang;
  // Lives here so it ONLY runs when the user is inside the authenticated
  // dashboard. Layout never mounts on the Login screen, so the timer
  // cannot fire during authentication or block the login flow.
  //
  // Coordinado entre pestañas (inactivityMonitor): el timestamp de última
  // actividad es compartido, así que una pestaña ociosa no cierra la sesión
  // (que comparten todas las pestañas del navegador) mientras otra esté activa.
  useEffect(() => {
    return startInactivityMonitor({
      timeoutMs: INACTIVITY_TIMEOUT_MS,
      warningMs: WARNING_BEFORE_MS,
      onWarn: () => {
        showToast(
          dictT('session.inactivityWarning', langRef.current),
          'warning',
          WARNING_BEFORE_MS,
        );
      },
      onTimeout: () => {
        // scope 'local': no revoca las sesiones de otros dispositivos.
        void signOutWithAudit('inactivity', 'local').catch((err) => {
          console.error('[SEC-5] inactivity signOut failed:', err);
        });
      },
    });
  }, []);

  return (
    <div className="flex h-[100dvh] overflow-clip bg-canvas">
      <Sidebar labels={t} isMobileOpen={isMobileOpen} onMobileClose={() => setIsMobileOpen(false)} />
      <div
        className={`flex-1 min-w-0 flex flex-col h-[100dvh] transition-[margin-left] duration-300 ease-in-out ml-0 ${isCollapsed ? 'md:ml-16' : 'md:ml-[240px]'}`}
      >
        <Header onMenuToggle={() => setIsMobileOpen(v => !v)} />
        <TrialBanner />
        <CompanyLimitBanner />
        <NoCompanySelectedBanner />
        <main className="flex-1 min-h-0 min-w-0 overflow-y-auto bg-canvas">
          <div key={pathname} className="max-w-[1600px] px-4 md:px-8 py-6 pb-24 page-enter">
            <Outlet />
          </div>
        </main>
      </div>
      <Suspense fallback={null}>
        <QuickGuide />
      </Suspense>
      <AiCopilotFab />
      <GlobalSpinner />
      <ToastContainer />
      <ConfirmDialogContainer />
      <AlertsPanel />
    </div>
  );
}
